import * as React from 'react';

import { DiagnosticRecord, UnassignedPost } from './diagnosticCard';
import {
  RecommendationCounts,
  RecommendationInfo,
  assignGrowthDiagnosticLink,
  diagnosticRecommendationsLink,
  diagnosticResultsLink,
  recommendationInfoFor,
} from './shared';

import { EXTRA_SMALL_ICON_BASE_SRC, Tooltip } from '../../../../Shared/index';

type StepStatus = 'done' | 'in-progress' | 'pending'
type StepOwner = 'You' | 'Students'

interface Step {
  label: string;
  status: StepStatus;
  owner: StepOwner;
  live?: boolean;
  detail?: string;
  detailHref?: string;
  waiting?: string;
  action?: { text: string, href: string, attention?: boolean, newTab?: boolean };
}

export function isAssignedPost(post?: DiagnosticRecord | UnassignedPost): post is DiagnosticRecord {
  return Boolean(post && (post as DiagnosticRecord).assigned_count !== undefined)
}

function completionStatus(completedCount: number, assignedCount: number): StepStatus {
  if (assignedCount > 0 && completedCount >= assignedCount) { return 'done' }
  if (completedCount > 0) { return 'in-progress' }
  return 'pending'
}

const STEP_STATUS_COPY: Record<StepStatus, string> = {
  'done': 'Complete.',
  'in-progress': 'In progress.',
  'pending': 'Not started.',
}

const waitingIcon = <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/clock.svg`} />
const checkCircleIcon = <img alt="" src={`${EXTRA_SMALL_ICON_BASE_SRC}/check-circle.svg`} />
const completedCheck = <span aria-hidden="true" className="step-complete-check">{checkCircleIcon}</span>


const StepRow = ({ step }: { step: Step }) => {
  const isFuture = step.status !== 'done' && !step.live
  const statusCopy = isFuture ? 'Waiting on earlier steps.' : STEP_STATUS_COPY[step.status]

  return (
    <li className={`diagnostic-step ${step.status} ${isFuture ? 'future' : ''}`}>
      <span aria-hidden="true" className="step-indicator">
        {step.status === 'done' && checkCircleIcon}
      </span>
      <span className="step-label">
        {step.label}
        <span className="sr-only">, {statusCopy}</span>
        <span className={`step-owner ${step.owner === 'You' ? 'you' : 'students'}`}>{step.owner}</span>
      </span>
      <span className="step-detail">
        {step.waiting && (
          <Tooltip
            isTabbable={false}
            tooltipText={step.waiting}
            tooltipTriggerText={<span aria-label={step.waiting} className="step-waiting" role="img">{waitingIcon}</span>}
          />
        )}
        {step.detail && step.detailHref && (
          <a className="focus-on-light step-detail-link" href={step.detailHref}>
            {step.status === 'done' && completedCheck}
            {step.detail}
          </a>
        )}
        {step.detail && !step.detailHref && (
          <span className="step-detail-text">
            {step.status === 'done' && completedCheck}
            {step.detail}
          </span>
        )}
        {step.action && (
          <a
            className={`quill-button extra-small outlined grey focus-on-light step-action ${step.action.attention ? 'attention' : ''}`}
            href={step.action.href}
            rel={step.action.newTab ? 'noopener noreferrer' : undefined}
            target={step.action.newTab ? '_blank' : undefined}
          >
            {step.action.text}
            {step.action.attention && <span className="sr-only"> (action needed)</span>}
            {step.action.newTab && <span className="sr-only"> (opens in a new window)</span>}
          </a>
        )}
      </span>
    </li>
  )
}

interface StepInputs {
  pre: DiagnosticRecord;
  post?: DiagnosticRecord | UnassignedPost;
  classroomId: number;
  preTestLabel: string;
  preRecommendations: RecommendationInfo;
  postRecommendations: RecommendationInfo;
  backingPackUnitId?: string;
  postBackingPackUnitId?: string;
}

function buildPreSteps({ pre, classroomId, preTestLabel, preRecommendations, backingPackUnitId }: StepInputs): Step[] {
  return [
    {
      label: `Assign ${preTestLabel}`,
      status: 'done',
      owner: 'You',
      live: true,
      detail: `${pre.assigned_count} of ${pre.assigned_count}`,
    },
    {
      label: `Complete ${preTestLabel}`,
      status: completionStatus(pre.completed_count, pre.assigned_count),
      owner: 'Students',
      live: true,
      detail: `${pre.completed_count} of ${pre.assigned_count}`,
      action: pre.completed_count > 0
        ? { text: 'View results', href: diagnosticResultsLink(pre.activity_id, classroomId, false), newTab: true }
        : undefined,
    },
    {
      label: 'Assign recommended practice',
      status: completionStatus(preRecommendations.assigned, pre.completed_count),
      owner: 'You',
      live: pre.completed_count > 0,
      detail: pre.completed_count > 0 ? `${Math.min(preRecommendations.assigned, pre.completed_count)} of ${pre.completed_count}` : undefined,
      waiting: pre.completed_count > 0 ? undefined : `Waiting for students to complete the ${preTestLabel}`,
      action: pre.completed_count > 0 && preRecommendations.assigned < pre.completed_count
        ? { text: 'Assign', href: diagnosticRecommendationsLink(pre.activity_id, classroomId, backingPackUnitId), attention: preRecommendations.assigned === 0, newTab: true }
        : undefined,
    },
    {
      label: 'Complete recommended practice',
      status: preRecommendations.assigned > 0 ? completionStatus(preRecommendations.completed_practice, preRecommendations.assigned) : 'pending',
      owner: 'Students',
      live: preRecommendations.assigned > 0,
      detail: preRecommendations.assigned > 0 ? `${preRecommendations.completed_practice} of ${preRecommendations.assigned}` : undefined,
      waiting: preRecommendations.assigned > 0 ? undefined : 'Waiting for recommended practice to be assigned',
    },
  ]
}

function buildPostSteps({ pre, post, classroomId, preRecommendations, postRecommendations, backingPackUnitId, postBackingPackUnitId }: StepInputs): Step[] {
  const postAssignedCount = isAssignedPost(post) ? post.assigned_count : 0
  const postTemplateId = post?.unit_template_id

  const postAssignReady = preRecommendations.assigned > 0
  const postFullyAssigned = postAssignedCount >= pre.assigned_count

  const steps: Step[] = [
    {
      label: 'Assign Growth Post',
      status: completionStatus(postAssignedCount, pre.assigned_count),
      owner: 'You',
      live: pre.completed_count > 0,
      detail: (postFullyAssigned || postAssignReady) ? `${Math.min(postAssignedCount, pre.assigned_count)} of ${pre.assigned_count}` : undefined,
      waiting: (!postFullyAssigned && !postAssignReady) ? 'Available after you assign recommended practice' : undefined,
      action: postTemplateId && !postFullyAssigned && postAssignReady ? {
        text: 'Assign',
        href: assignGrowthDiagnosticLink(pre.activity_id, classroomId, backingPackUnitId),
        attention: postAssignedCount === 0,
        newTab: true,
      } : undefined,
    },
  ]

  if (isAssignedPost(post)) {
    steps.push({
      label: 'Complete Growth Post',
      status: completionStatus(post.completed_count, post.assigned_count),
      owner: 'Students',
      live: true,
      detail: `${post.completed_count} of ${post.assigned_count}`,
      action: post.completed_count > 0
        ? { text: 'View results', href: diagnosticResultsLink(pre.post_test_id || post.activity_id, classroomId, true), newTab: true }
        : undefined,
    })
    steps.push({
      label: 'Assign recommended practice',
      status: completionStatus(postRecommendations.assigned, post.completed_count),
      owner: 'You',
      live: post.completed_count > 0,
      detail: post.completed_count > 0 ? `${Math.min(postRecommendations.assigned, post.completed_count)} of ${post.completed_count}` : undefined,
      waiting: post.completed_count > 0 ? undefined : 'Waiting for students to complete the Growth Post',
      action: post.completed_count > 0 && postRecommendations.assigned < post.completed_count
        ? { text: 'Assign', href: diagnosticRecommendationsLink(post.activity_id, classroomId, postBackingPackUnitId), attention: postRecommendations.assigned === 0, newTab: true }
        : undefined,
    })
    steps.push({
      label: 'Complete recommended practice',
      status: postRecommendations.assigned > 0 ? completionStatus(postRecommendations.completed_practice, postRecommendations.assigned) : 'pending',
      owner: 'Students',
      live: postRecommendations.assigned > 0,
      detail: postRecommendations.assigned > 0 ? `${postRecommendations.completed_practice} of ${postRecommendations.assigned}` : undefined,
      waiting: postRecommendations.assigned > 0 ? undefined : 'Waiting for recommended practice to be assigned',
    })
  } else {
    steps.push({ label: 'Complete Growth Post', owner: 'Students', status: 'pending', live: false, waiting: 'Waiting for the Growth Post to be assigned' })
    steps.push({ label: 'Assign recommended practice', owner: 'You', status: 'pending', live: false, waiting: 'Waiting for students to complete the Growth Post' })
    steps.push({ label: 'Complete recommended practice', owner: 'Students', status: 'pending', live: false, waiting: 'Waiting for recommended practice to be assigned' })
  }

  return steps
}

interface DiagnosticJourneyProps {
  pre: DiagnosticRecord;
  post?: DiagnosticRecord | UnassignedPost;
  classroomId: number;
  recommendationCounts: RecommendationCounts;
  postTestExists: boolean;
  preTestLabel: string;
  backingPackUnitId?: string;
  postBackingPackUnitId?: string;
}

const DiagnosticJourney = ({ pre, post, classroomId, recommendationCounts, postTestExists, preTestLabel, backingPackUnitId, postBackingPackUnitId }: DiagnosticJourneyProps) => {
  const preRecommendations = recommendationInfoFor(recommendationCounts, pre.activity_id, [classroomId])
  const postRecommendations = isAssignedPost(post) ? recommendationInfoFor(recommendationCounts, post.activity_id, [classroomId]) : { assigned: 0, completed_practice: 0 }

  const stepInputs: StepInputs = { pre, post, classroomId, preTestLabel, preRecommendations, postRecommendations, backingPackUnitId, postBackingPackUnitId }
  const preSteps = buildPreSteps(stepInputs)
  const postSteps = postTestExists ? buildPostSteps(stepInputs) : []

  return (
    <div className="pack-card-journey">
      <div className="journey-heading">
        <h4>Diagnostic Journey</h4>
      </div>
      {postSteps.length ? (
        <React.Fragment>
          <div className="journey-phase">
            <span className="pre-badge">Pre</span>
            <ul className="diagnostic-steps">
              {preSteps.map(step => <StepRow key={step.label} step={step} />)}
            </ul>
          </div>
          <div className="journey-phase">
            <span className="post-badge">Post</span>
            <ul className="diagnostic-steps">
              {postSteps.map(step => <StepRow key={step.label} step={step} />)}
            </ul>
          </div>
        </React.Fragment>
      ) : (
        <ul className="diagnostic-steps">
          {preSteps.map(step => <StepRow key={step.label} step={step} />)}
        </ul>
      )}
    </div>
  )
}

export default DiagnosticJourney
